import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import EventCard from "./cards/EventCard";
import { fetchCommunityEvents, fetchCommercialEvents } from "../../services/events";
import { fetchMasterCities } from "../../services/locationServices";

export default function EventsSection() {
  const [eventCards, setEventCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [locationName, setLocationName] = useState("Houston");

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      try {
        const geohash = localStorage.getItem("geohash") || "9v6m";
        
        const locationResponse = await fetchMasterCities({ geohash });
        let cityName = "Austin";
        
        if (locationResponse?.data?.[0]) {
          const city = locationResponse.data[0].city;
          const state = locationResponse.data[0].state;
          cityName = city;
          setLocationName(state ? `${city}, ${state}` : city);
        }

        const [commercialResponse, communityResponse] = await Promise.all([
          fetchCommercialEvents({
            city: cityName,
            page: 1,
            limit: 5,
          }),
          fetchCommunityEvents({
            city: cityName,
            page: 1,
            limit: 5,
          }),
        ]);

        const commercialEvents = commercialResponse?.message || commercialResponse?.data || [];
        const communityEvents = communityResponse?.data || [];

        const allEvents = [...commercialEvents, ...communityEvents].slice(0, 5);

        const formattedEvents = allEvents.map((event) => {
          const eventDate = event.event_date || event.date || "";
          const dateParts = eventDate.split(",");
          const formattedDate = dateParts[0]?.trim() || "";
          const formattedTime = dateParts[2]?.trim() || event.time || "";

          return {
            id: event.event_id || event.id,
            title: event.name || event.title || "Event",
            location: event.location || `${event.city || ""}, ${event.state || ""}`.trim() || "Location TBD",
            date: formattedDate || "Date TBD",
            time: formattedTime,
            price: event.price || "",
            posterImage: event.cover_image || event.image || "/images/event-placeholder.png",
          };
        });

        setEventCards(formattedEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
        setEventCards([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();

    const handleGeohashChange = () => {
      fetchEvents();
    };

    window.addEventListener("geohashChanged", handleGeohashChange);
    window.addEventListener("storage", (e) => {
      if (e.key === "geohash") {
        handleGeohashChange();
      }
    });

    return () => {
      window.removeEventListener("geohashChanged", handleGeohashChange);
      window.removeEventListener("storage", handleGeohashChange);
    };
  }, []);

  return (
    <section className="bg-gray-50" style={{ 
      paddingTop: 'clamp(96px, 8.33vw, 160px)',
      paddingBottom: 'clamp(96px, 8.33vw, 160px)',
      paddingLeft: 'clamp(16px, 1.25vw, 24px)',
      paddingRight: 'clamp(16px, 1.25vw, 24px)'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@700&family=Afacad:wght@400;500;600&display=swap');
        
        .fraunces-bold {
          font-family: 'Fraunces', serif;
          font-weight: 700;
        }
        
        .afacad-font {
          font-family: 'Afacad', sans-serif;
        }
      `}</style>

      <div className="mx-auto" style={{ maxWidth: '1920px', margin: '0 auto' }}>
        {/* Header */}
        <div className="text-center" style={{ marginBottom: 'clamp(32px, 2.78vw, 54px)' }}>
          <h2
            className="fraunces-bold"
            style={{ 
              color: "#7B189F",
              fontSize: 'clamp(18px, 1.31vw, 26px)',
              fontWeight: 700,
              lineHeight: '1.2',
              marginBottom: 'clamp(12px, 0.83vw, 16px)'
            }}
          >
            Events In {locationName}
          </h2>
          <p className="text-gray-600 max-w-xl sm:max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto px-4 fraunces-bold" style={{ 
            fontSize: 'clamp(16px, 1.11vw, 22px)', 
            fontWeight: 700,
            lineHeight: '1.4',
            marginTop: 0
          }}>
            There are many variations of passages of Lorem Ipsum available, but
            the majority have suffered alteration in some form.
          </p>
        </div>

        {/* Events Grid - Responsive grid layout */}
        {isLoading ? (
          <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
            <div className="text-gray-500 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B189F] mx-auto mb-4"></div>
              <p className="afacad-font" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>Loading events...</p>
            </div>
          </div>
        ) : eventCards.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5 gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-7 2xl:gap-8">
            {/* Mobile: Show only first 4 cards */}
            {eventCards.slice(0, 4).map((event) => (
              <EventCard key={event.id} {...event} className="lg:hidden" />
            ))}

            {/* Desktop: Show all cards (up to 5) */}
            {eventCards.map((event) => (
              <EventCard key={event.id} {...event} className="hidden lg:block" />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 afacad-font" style={{ fontSize: 'clamp(14px, 1.04vw, 16px)' }}>
              No events available at this time.
            </p>
          </div>
        )}

        {/* View More */}
        {eventCards.length > 0 && (
          <div className="text-center" style={{ marginTop: 'clamp(32px, 2.78vw, 54px)' }}>
            <Link
              to="/events"
              className="afacad-font hover:opacity-80 flex items-center justify-center mx-auto transition-opacity gap-2"
              style={{ 
                color: "#411B26",
                fontSize: 'clamp(12px, 0.83vw, 16px)',
                fontWeight: 600
              }}
            >
              View More
              <img
                src="/landing/more.png"
                alt="More"
                style={{ width: 'clamp(30px, 2.17vw, 43px)', height: 'clamp(30px, 2.17vw, 43px)' }}
              />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
